import Head from 'next/head';
import { APP_NAME, APP_NAME_URL } from '../constants';
import { PageProviders } from 'app/features/silo/PageProviders';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import {
  MarketsData,
  MarketsDataEncoded,
  RUNTIME_PROTOCOL_INSTANCES,
  ServerPublicClientServiceLive,
  logRpcMetrics,
} from '@silo/types';
import { Cause, Console, Effect, Logger, pipe } from 'effect';
import { getDisplayMarkets, KvService } from '@silo/data';
import { Schema } from '@effect/schema';
import { kv } from '@vercel/kv';
import { MarketsV2 } from 'app/features/markets-v2';
import { useQueryMarketsData } from 'app/queries/use-query-markets-data';

const Main = ({ data }: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element => {
  const staticData = pipe(data, Schema.decodeSync(MarketsData));
  const { data: queryData } = useQueryMarketsData();

  const marketsData = queryData ?? staticData;

  return (
    <>
      <Head>
        <title>{APP_NAME_URL}</title>
        <meta name="description" content={APP_NAME} />
      </Head>

      <MarketsV2 marketsData={marketsData} />
    </>
  );
};

interface MarketsStaticProps {
  data: MarketsDataEncoded;
}

export const getStaticProps: GetStaticProps<MarketsStaticProps> = async () => {
  const data = await pipe(
    getDisplayMarkets(RUNTIME_PROTOCOL_INSTANCES),
    Effect.andThen(Schema.encode(MarketsData)),
    Effect.tap(() => logRpcMetrics()),
    Effect.tapErrorCause((cause) => Console.log(Cause.pretty(cause))),
    Effect.withLogSpan('elapsed'),
    Effect.provideService(KvService, kv),
    Effect.provide(ServerPublicClientServiceLive),
    Effect.provide(Logger.pretty),
    Effect.runPromise
  );

  return {
    props: { data },
    revalidate: 60,
  };
};

export default Main;
Main.Provider = PageProviders;
